import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import type { AppointmentType } from '../../calendar/types/Calendar';
import {
  addConsultationTemplateField,
  approveConsultationTemplate,
  createConsultationTemplate,
  getConsultationTemplateById,
  listConsultationFieldDefinitions,
  listConsultationTypes,
  listConsultationTemplates,
  rejectConsultationTemplate,
  removeConsultationTemplate,
  removeConsultationTemplateField,
  updateConsultationTemplate,
  updateConsultationTemplateField,
} from '../services/consultationService';
import type {
  AddConsultationTemplateFieldInput,
  ConsultationFieldDefinition,
  ConsultationTemplate,
  ConsultationTemplateField,
  CreateConsultationTemplateInput,
  UpdateConsultationTemplateInput,
  UpdateConsultationTemplateFieldInput,
} from '../types/Consultation';
import styles from './ConsultationTemplatesPage.module.css';
import { ConsultationAuthoringBundleMenu } from './ConsultationAuthoringBundleMenu';

type TemplateEditorState = {
  name: string;
  code: string;
  domainProfileKey: string;
  appointmentTypeId: string;
  subjectType: string;
  description: string;
  isActive: boolean;
};

type AssignmentEditorState = {
  fieldDefinitionId: string;
  sectionKey: string;
  sortOrder: string;
  isRequired: boolean;
};

const emptyTemplateEditorState = (): TemplateEditorState => ({
  name: '',
  code: '',
  domainProfileKey: '',
  appointmentTypeId: '',
  subjectType: '',
  description: '',
  isActive: true,
});

const emptyAssignmentEditorState = (): AssignmentEditorState => ({
  fieldDefinitionId: '',
  sectionKey: '',
  sortOrder: '0',
  isRequired: false,
});

const toTemplateEditorState = (template: ConsultationTemplate): TemplateEditorState => ({
  name: template.name,
  code: template.code ?? '',
  domainProfileKey: template.domainProfileKey,
  appointmentTypeId: template.appointmentTypeId ?? '',
  subjectType: template.subjectType ?? '',
  description: template.description ?? '',
  isActive: template.isActive,
});

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

type TemplateStatusFilter = 'all' | 'active' | 'inactive';
type TemplateSortMode = 'nameAsc' | 'updatedDesc' | 'fieldsDesc';

const VALID_TEMPLATE_STATUS_FILTERS = new Set<TemplateStatusFilter>(['all', 'active', 'inactive']);
const VALID_TEMPLATE_SORT_MODES = new Set<TemplateSortMode>(['nameAsc', 'updatedDesc', 'fieldsDesc']);

const matchesTemplateFilter = (template: ConsultationTemplate, query: string, statusFilter: TemplateStatusFilter) => {
  if (statusFilter === 'active' && !template.isActive) {
    return false;
  }

  if (statusFilter === 'inactive' && template.isActive) {
    return false;
  }

  if (!query) {
    return true;
  }

  const haystack = [template.name, template.code, template.domainProfileKey, template.subjectType, template.description]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();

  return haystack.includes(query);
};

const sortTemplates = (items: ConsultationTemplate[], sortMode: TemplateSortMode) => {
  const sorted = [...items];

  switch (sortMode) {
    case 'updatedDesc':
      return sorted.sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());
    case 'fieldsDesc':
      return sorted.sort((left, right) => (right.fields?.length ?? 0) - (left.fields?.length ?? 0) || left.name.localeCompare(right.name));
    case 'nameAsc':
    default:
      return sorted.sort((left, right) => left.name.localeCompare(right.name));
  }
};

const getAiStatusLabel = (template: ConsultationTemplate, t: ReturnType<typeof useTranslation>['t']) => {
  if (!template.createdByAi) {
    return null;
  }

  if (template.aiStatus === 'approved') {
    return t('aiApproved', { defaultValue: 'AI approved' });
  }

  if (template.aiStatus === 'rejected') {
    return t('aiRejected', { defaultValue: 'AI rejected' });
  }

  return t('aiPendingApproval', { defaultValue: 'AI draft - pending approval' });
};

export const ConsultationTemplatesPage: React.FC = () => {
  const { t } = useTranslation(['consultations', 'calendar', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [savingTemplate, setSavingTemplate] = React.useState(false);
  const [savingAssignment, setSavingAssignment] = React.useState(false);
  const [templates, setTemplates] = React.useState<ConsultationTemplate[]>([]);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [fieldDefinitions, setFieldDefinitions] = React.useState<ConsultationFieldDefinition[]>([]);
  const selectedTemplateIdParam = searchParams.get('templateId');
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(selectedTemplateIdParam);
  const [selectedTemplate, setSelectedTemplate] = React.useState<ConsultationTemplate | null>(null);
  const [templateEditorState, setTemplateEditorState] = React.useState<TemplateEditorState>(() => emptyTemplateEditorState());
  const [assignmentEditorState, setAssignmentEditorState] = React.useState<AssignmentEditorState>(() => emptyAssignmentEditorState());
  const [error, setError] = React.useState('');
  const [statusMessage, setStatusMessage] = React.useState('');

  const searchTerm = searchParams.get('q') ?? '';
  const statusFilter = VALID_TEMPLATE_STATUS_FILTERS.has((searchParams.get('status') as TemplateStatusFilter) ?? 'all')
    ? ((searchParams.get('status') as TemplateStatusFilter) ?? 'all')
    : 'all';
  const sortMode = VALID_TEMPLATE_SORT_MODES.has((searchParams.get('sort') as TemplateSortMode) ?? 'nameAsc')
    ? ((searchParams.get('sort') as TemplateSortMode) ?? 'nameAsc')
    : 'nameAsc';
  const hasActiveFilters = Boolean(searchTerm.trim()) || statusFilter !== 'all' || sortMode !== 'nameAsc';

  const updateQueryParam = React.useCallback((key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClearFilters = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('status');
    next.delete('sort');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCopyLink = React.useCallback(async () => {
    setError('');
    setStatusMessage('');

    if (!navigator.clipboard?.writeText) {
      setError(t('copyLinkNotSupported', { defaultValue: 'Copy link is not supported in this browser.' }));
      return;
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      setStatusMessage(t('consultationTemplatesLinkCopied', { defaultValue: 'Template page link copied.' }));
    } catch {
      setError(t('consultationTemplatesLinkCopyFailed', { defaultValue: 'Failed to copy template page link.' }));
    }
  }, [t]);

  React.useEffect(() => {
    setSelectedTemplateId(selectedTemplateIdParam);
  }, [selectedTemplateIdParam]);

  const loadBaseData = React.useCallback(async (nextSelectedTemplateId?: string | null) => {
    setLoading(true);
    setError('');

    try {
      const [loadedTemplates, loadedFieldDefinitions, loadedAppointmentTypes] = await Promise.all([
        listConsultationTemplates(),
        listConsultationFieldDefinitions({ isActive: true }),
        listConsultationTypes({ status: 'active' }),
      ]);

      const sortedTemplates = sortTemplates(loadedTemplates, 'nameAsc');
      const sortedFieldDefinitions = [...loadedFieldDefinitions].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
      setTemplates(sortedTemplates);
      setFieldDefinitions(sortedFieldDefinitions);
      setAppointmentTypes(loadedAppointmentTypes);

      const resolvedId = nextSelectedTemplateId === undefined ? selectedTemplateIdParam : nextSelectedTemplateId;
      if (!resolvedId) {
        setSelectedTemplateId(null);
        setSelectedTemplate(null);
        setTemplateEditorState(emptyTemplateEditorState());
        setAssignmentEditorState((current) => ({ ...emptyAssignmentEditorState(), fieldDefinitionId: current.fieldDefinitionId }));
        return;
      }

      const detailed = await getConsultationTemplateById(resolvedId);
      if (!detailed) {
        setSelectedTemplateId(null);
        setSelectedTemplate(null);
        setTemplateEditorState(emptyTemplateEditorState());
        setAssignmentEditorState(emptyAssignmentEditorState());
        return;
      }

      setSelectedTemplateId(detailed.id);
      setSelectedTemplate(detailed);
      setTemplateEditorState(toTemplateEditorState(detailed));
      setAssignmentEditorState((current) => ({
        ...current,
        fieldDefinitionId: current.fieldDefinitionId && sortedFieldDefinitions.some((item) => item.id === current.fieldDefinitionId)
          ? current.fieldDefinitionId
          : (sortedFieldDefinitions[0]?.id ?? ''),
      }));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('consultationTemplatesLoadFailed', { defaultValue: 'Failed to load consultation templates.' }));
    } finally {
      setLoading(false);
    }
  }, [selectedTemplateIdParam, t]);

  React.useEffect(() => {
    void loadBaseData(selectedTemplateIdParam ?? undefined);
  }, [loadBaseData, selectedTemplateIdParam]);

  const handleSelectTemplate = React.useCallback(async (templateId: string) => {
    setError('');
    setStatusMessage('');
    updateQueryParam('templateId', templateId);
    await loadBaseData(templateId);
  }, [loadBaseData, updateQueryParam]);

  const handleCreateNewTemplate = React.useCallback(() => {
    updateQueryParam('templateId', undefined);
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setTemplateEditorState(emptyTemplateEditorState());
    setAssignmentEditorState((current) => ({ ...emptyAssignmentEditorState(), fieldDefinitionId: current.fieldDefinitionId || fieldDefinitions[0]?.id || '' }));
    setError('');
    setStatusMessage('');
  }, [fieldDefinitions, updateQueryParam]);

  const updateTemplateField = React.useCallback(<K extends keyof TemplateEditorState>(key: K, value: TemplateEditorState[K]) => {
    setTemplateEditorState((current) => ({ ...current, [key]: value }));
  }, []);

  const updateAssignmentField = React.useCallback(<K extends keyof AssignmentEditorState>(key: K, value: AssignmentEditorState[K]) => {
    setAssignmentEditorState((current) => ({ ...current, [key]: value }));
  }, []);

  const handleSaveTemplate = React.useCallback(async () => {
    if (!templateEditorState.name.trim()) {
      setError(t('consultationTemplateNameRequired', { defaultValue: 'Template name is required.' }));
      return;
    }

    if (!templateEditorState.domainProfileKey.trim()) {
      setError(t('consultationTemplateDomainRequired', { defaultValue: 'Domain profile key is required.' }));
      return;
    }

    const basePayload = {
      name: templateEditorState.name.trim(),
      code: normalizeOptionalText(templateEditorState.code),
      domainProfileKey: templateEditorState.domainProfileKey.trim(),
      subjectType: normalizeOptionalText(templateEditorState.subjectType),
      description: normalizeOptionalText(templateEditorState.description),
      isActive: templateEditorState.isActive,
    };

    const createPayload: CreateConsultationTemplateInput = {
      ...basePayload,
      appointmentTypeId: normalizeOptionalText(templateEditorState.appointmentTypeId),
    };

    const updatePayload: UpdateConsultationTemplateInput = {
      ...basePayload,
      appointmentTypeId: normalizeOptionalText(templateEditorState.appointmentTypeId),
      clearAppointmentType: !normalizeOptionalText(templateEditorState.appointmentTypeId),
    };

    setSavingTemplate(true);
    setError('');
    setStatusMessage('');

    try {
      const saved = selectedTemplateId
        ? await updateConsultationTemplate(selectedTemplateId, updatePayload)
        : await createConsultationTemplate(createPayload);

      if (!saved) {
        setError(t('consultationTemplateSaveFailed', { defaultValue: 'Failed to save consultation template.' }));
        return;
      }

      setStatusMessage(selectedTemplateId
        ? t('consultationTemplateUpdated', { defaultValue: 'Consultation template updated.' })
        : t('consultationTemplateCreated', { defaultValue: 'Consultation template created.' }));
      updateQueryParam('templateId', saved.id);
      await loadBaseData(saved.id);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('consultationTemplateSaveFailed', { defaultValue: 'Failed to save consultation template.' }));
    } finally {
      setSavingTemplate(false);
    }
  }, [loadBaseData, selectedTemplateId, t, templateEditorState, updateQueryParam]);

  const handleDeleteTemplate = React.useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }

    const shouldDelete = window.confirm(
      t('consultationTemplateDeleteConfirm', {
        defaultValue: 'Delete "{{name}}"? Assigned template fields will be removed too. This cannot be undone.',
        name: selectedTemplate.name,
      }),
    );

    if (!shouldDelete) {
      return;
    }

    setSavingTemplate(true);
    setError('');
    setStatusMessage('');

    try {
      await removeConsultationTemplate(selectedTemplate.id);
      setStatusMessage(t('consultationTemplateDeleted', { defaultValue: 'Consultation template deleted.' }));
      updateQueryParam('templateId', undefined);
      await loadBaseData(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('consultationTemplateDeleteFailed', { defaultValue: 'Failed to delete consultation template.' }));
    } finally {
      setSavingTemplate(false);
    }
  }, [loadBaseData, selectedTemplate, t, updateQueryParam]);

  const handleApproveTemplate = React.useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }

    setSavingTemplate(true);
    setError('');
    setStatusMessage('');
    try {
      const approved = await approveConsultationTemplate(selectedTemplate.id);
      if (!approved) {
        setError(t('consultationTemplateApproveFailed', { defaultValue: 'Failed to approve consultation template.' }));
        return;
      }

      setStatusMessage(t('consultationTemplateApproved', { defaultValue: 'Consultation template approved.' }));
      await loadBaseData(approved.id);
    } catch (approveError) {
      setError(approveError instanceof Error ? approveError.message : t('consultationTemplateApproveFailed', { defaultValue: 'Failed to approve consultation template.' }));
    } finally {
      setSavingTemplate(false);
    }
  }, [loadBaseData, selectedTemplate, t]);

  const handleRejectTemplate = React.useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }

    setSavingTemplate(true);
    setError('');
    setStatusMessage('');
    try {
      const rejected = await rejectConsultationTemplate(selectedTemplate.id);
      if (!rejected) {
        setError(t('consultationTemplateRejectFailed', { defaultValue: 'Failed to reject consultation template.' }));
        return;
      }

      setStatusMessage(t('consultationTemplateRejected', { defaultValue: 'Consultation template rejected.' }));
      await loadBaseData(rejected.id);
    } catch (rejectError) {
      setError(rejectError instanceof Error ? rejectError.message : t('consultationTemplateRejectFailed', { defaultValue: 'Failed to reject consultation template.' }));
    } finally {
      setSavingTemplate(false);
    }
  }, [loadBaseData, selectedTemplate, t]);

  const handleAddAssignment = React.useCallback(async () => {
    if (!selectedTemplateId) {
      setError(t('consultationTemplateSelectFirst', { defaultValue: 'Create or select a template before assigning fields.' }));
      return;
    }

    if (!assignmentEditorState.fieldDefinitionId) {
      setError(t('consultationTemplateFieldDefinitionRequired', { defaultValue: 'Choose a field definition to assign.' }));
      return;
    }

    const sortOrder = Number(assignmentEditorState.sortOrder);
    if (!Number.isFinite(sortOrder)) {
      setError(t('consultationTemplateFieldSortInvalid', { defaultValue: 'Assignment sort order must be a valid number.' }));
      return;
    }

    const payload: AddConsultationTemplateFieldInput = {
      fieldDefinitionId: assignmentEditorState.fieldDefinitionId,
      sectionKey: normalizeOptionalText(assignmentEditorState.sectionKey),
      sortOrder,
      isRequired: assignmentEditorState.isRequired,
    };

    setSavingAssignment(true);
    setError('');
    setStatusMessage('');

    try {
      const created = await addConsultationTemplateField(selectedTemplateId, payload);
      if (!created) {
        setError(t('consultationTemplateFieldSaveFailed', { defaultValue: 'Failed to assign field definition to template.' }));
        return;
      }

      setStatusMessage(t('consultationTemplateFieldAdded', { defaultValue: 'Field definition assigned to template.' }));
      setAssignmentEditorState((current) => ({ ...emptyAssignmentEditorState(), fieldDefinitionId: current.fieldDefinitionId }));
      await loadBaseData(selectedTemplateId);
    } catch (assignmentError) {
      setError(assignmentError instanceof Error ? assignmentError.message : t('consultationTemplateFieldSaveFailed', { defaultValue: 'Failed to assign field definition to template.' }));
    } finally {
      setSavingAssignment(false);
    }
  }, [assignmentEditorState, loadBaseData, selectedTemplateId, t]);

  const handleUpdateAssignment = React.useCallback(async (templateField: ConsultationTemplateField, input: UpdateConsultationTemplateFieldInput) => {
    if (!selectedTemplateId) {
      return;
    }

    setSavingAssignment(true);
    setError('');
    setStatusMessage('');

    try {
      await updateConsultationTemplateField(selectedTemplateId, templateField.fieldDefinitionId, input);
      setStatusMessage(t('consultationTemplateFieldUpdated', { defaultValue: 'Template field assignment updated.' }));
      await loadBaseData(selectedTemplateId);
    } catch (assignmentError) {
      setError(assignmentError instanceof Error ? assignmentError.message : t('consultationTemplateFieldUpdateFailed', { defaultValue: 'Failed to update template field assignment.' }));
    } finally {
      setSavingAssignment(false);
    }
  }, [loadBaseData, selectedTemplateId, t]);

  const handleRemoveAssignment = React.useCallback(async (templateField: ConsultationTemplateField) => {
    if (!selectedTemplateId) {
      return;
    }

    setSavingAssignment(true);
    setError('');
    setStatusMessage('');

    try {
      await removeConsultationTemplateField(selectedTemplateId, templateField.fieldDefinitionId);
      setStatusMessage(t('consultationTemplateFieldRemoved', { defaultValue: 'Field definition removed from template.' }));
      await loadBaseData(selectedTemplateId);
    } catch (assignmentError) {
      setError(assignmentError instanceof Error ? assignmentError.message : t('consultationTemplateFieldRemoveFailed', { defaultValue: 'Failed to remove field definition from template.' }));
    } finally {
      setSavingAssignment(false);
    }
  }, [loadBaseData, selectedTemplateId, t]);

  const assignedFieldIds = React.useMemo(() => new Set(selectedTemplate?.fields?.map((item) => item.fieldDefinitionId) ?? []), [selectedTemplate?.fields]);
  const appointmentTypeNameById = React.useMemo(() => {
    const next: Record<string, string> = {};
    for (const item of appointmentTypes) {
      next[item.id] = item.name;
    }
    return next;
  }, [appointmentTypes]);
  const filteredTemplates = React.useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLocaleLowerCase();
    return sortTemplates(
      templates.filter((template) => matchesTemplateFilter(template, normalizedQuery, statusFilter)),
      sortMode,
    );
  }, [searchTerm, sortMode, statusFilter, templates]);
  const availableFieldDefinitions = React.useMemo(
    () => fieldDefinitions.filter((item) => !assignedFieldIds.has(item.id)),
    [assignedFieldIds, fieldDefinitions],
  );

  React.useEffect(() => {
    if (!assignmentEditorState.fieldDefinitionId && availableFieldDefinitions[0]?.id) {
      setAssignmentEditorState((current) => ({ ...current, fieldDefinitionId: availableFieldDefinitions[0]?.id ?? '' }));
    }
  }, [assignmentEditorState.fieldDefinitionId, availableFieldDefinitions]);

  const getFieldDefinition = React.useCallback((fieldDefinitionId: string) => fieldDefinitions.find((item) => item.id === fieldDefinitionId) ?? null, [fieldDefinitions]);

  return (
    <div className={styles.page}>
      <Breadcrumb
        items={[
          { label: t('consultations', { defaultValue: 'Consultations' }), path: '/consultations' },
          { label: t('consultationTemplates', { defaultValue: 'Templates' }), path: '/consultations/templates' },
        ]}
      />

      <section className={styles.hero}>
        <div>
          <h1 className={styles.title}>{t('consultationTemplates', { defaultValue: 'Consultation templates' })}</h1>
          <p className={styles.subtitle}>
            {t('consultationTemplatesIntro', {
              defaultValue: 'Manage reusable consultation templates and decide which structured field definitions each template should require.',
            })}
          </p>
        </div>
        <div className={styles.heroActions}>
          <Link className="btn btn-secondary" to="/consultations">
            {t('backToConsultations', { defaultValue: 'Back to consultations' })}
          </Link>
          <Link className="btn btn-secondary" to="/consultations/types">
            {t('manageConsultationTypes', { defaultValue: 'Manage consultation types' })}
          </Link>
          <Link className="btn btn-secondary" to="/consultations/field-definitions">
            {t('manageFieldDefinitions', { defaultValue: 'Manage field definitions' })}
          </Link>
          <button className="btn btn-secondary" type="button" onClick={() => void handleCopyLink()}>
            {t('copyLink', { ns: 'common', defaultValue: 'Copy link' })}
          </button>
          <ConsultationAuthoringBundleMenu
            scope="templates"
            fieldDefinitions={fieldDefinitions}
            templates={templates}
            disabled={savingTemplate || savingAssignment}
            onCompleted={(message) => {
              setError('');
              setStatusMessage(message);
            }}
            onError={(message) => {
              setStatusMessage('');
              setError(message);
            }}
            onImported={() => void loadBaseData(selectedTemplateId)}
          />
          <button className="btn btn-primary" type="button" onClick={handleCreateNewTemplate}>
            {t('newTemplate', { defaultValue: 'New template' })}
          </button>
        </div>
      </section>

      {(error || statusMessage) ? (
        <section className={styles.messages}>
          {error ? <p className={styles.error}>{error}</p> : null}
          {statusMessage ? <p className={styles.success}>{statusMessage}</p> : null}
        </section>
      ) : null}

      {loading ? (
        <Spinner />
      ) : (
        <section className={styles.layout}>
          <aside className={styles.listPanel}>
            <div className={styles.panelHeader}>
              <h2>{t('existingTemplates', { defaultValue: 'Existing templates' })}</h2>
              <span className={styles.count}>{filteredTemplates.length}/{templates.length}</span>
            </div>
            <div className={styles.filters}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('search', { ns: 'common', defaultValue: 'Search' })}</label>
                <input
                  className="text-input"
                  value={searchTerm}
                  onChange={(event) => updateQueryParam('q', event.target.value.trim() ? event.target.value : undefined)}
                  placeholder={t('searchTemplatesPlaceholder', {
                    defaultValue: 'Search by name, code, domain, subject, or status',
                  })}
                />
              </div>

              <div className={styles.filterGrid}>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('status', { defaultValue: 'Status' })}</label>
                  <select className="text-input" value={statusFilter} onChange={(event) => updateQueryParam('status', event.target.value)}>
                    <option value="all">{t('allStatuses', { defaultValue: 'All statuses' })}</option>
                    <option value="active">{t('activeOnly', { defaultValue: 'Active only' })}</option>
                    <option value="inactive">{t('inactiveOnly', { defaultValue: 'Inactive only' })}</option>
                  </select>
                </div>

                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('sortBy', { defaultValue: 'Sort by' })}</label>
                  <select className="text-input" value={sortMode} onChange={(event) => updateQueryParam('sort', event.target.value)}>
                    <option value="nameAsc">{t('sortByName', { defaultValue: 'Name (A-Z)' })}</option>
                    <option value="updatedDesc">{t('sortByRecentlyUpdated', { defaultValue: 'Recently updated' })}</option>
                    <option value="fieldsDesc">{t('sortByAssignedFields', { defaultValue: 'Most assigned fields' })}</option>
                  </select>
                </div>
              </div>

              {hasActiveFilters ? (
                <div className={styles.footerInline}>
                  <button className="btn btn-secondary" type="button" onClick={handleClearFilters}>
                    {t('clearFilters', { ns: 'common', defaultValue: 'Clear filters' })}
                  </button>
                </div>
              ) : null}
            </div>

            {templates.length === 0 ? (
              <div className={styles.empty}>{t('consultationTemplatesEmpty', { defaultValue: 'No consultation templates exist yet.' })}</div>
            ) : filteredTemplates.length === 0 ? (
              <div className={styles.empty}>{t('consultationTemplatesNoMatch', { defaultValue: 'No consultation templates match the current filters.' })}</div>
            ) : (
              <div className={styles.templateList}>
                {filteredTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={`${styles.templateCard} ${template.id === selectedTemplateId ? styles.templateCardActive : ''}`}
                    onClick={() => void handleSelectTemplate(template.id)}
                  >
                    <div className={styles.templateHeader}>
                      <strong>{template.name}</strong>
                      <div>
                        <span className={`${styles.badge} ${template.isActive ? styles.badgeActive : styles.badgeMuted}`}>
                          {template.isActive ? t('active', { ns: 'common', defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' })}
                        </span>
                        {getAiStatusLabel(template, t) ? (
                          <span className={`${styles.badge} ${styles.badgeMuted}`}>
                            {getAiStatusLabel(template, t)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className={styles.templateMeta}>
                      <span>{template.domainProfileKey}</span>
                      <span>{template.appointmentTypeId ? (appointmentTypeNameById[template.appointmentTypeId] || template.appointmentTypeId) : t('allConsultationTypes', { defaultValue: 'All consultation types' })}</span>
                      <span>{template.subjectType || t('allSubjects', { defaultValue: 'All subjects' })}</span>
                    </div>
                    <p className={styles.templateDescription}>{template.description || t('noDescription', { defaultValue: 'No description' })}</p>
                    <span className={styles.timestamp}>{t('updated', { ns: 'common', defaultValue: 'Updated' })}: {formatDateTime(template.updatedAt)}</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <section className={styles.editorPanel}>
            <div className={styles.panelHeader}>
              <h2>{selectedTemplateId ? t('editTemplate', { defaultValue: 'Edit template' }) : t('createTemplate', { defaultValue: 'Create template' })}</h2>
              {selectedTemplateId ? <span className={styles.count}>{selectedTemplateId.slice(0, 8)}</span> : null}
            </div>

            <div className={styles.formGrid}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('name', { ns: 'common', defaultValue: 'Name' })}</label>
                <input className="text-input" value={templateEditorState.name} onChange={(event) => updateTemplateField('name', event.target.value)} maxLength={200} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('code', { defaultValue: 'Code' })}</label>
                <input className="text-input" value={templateEditorState.code} onChange={(event) => updateTemplateField('code', event.target.value)} maxLength={100} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('domainProfileKey', { defaultValue: 'Domain profile key' })}</label>
                <input className="text-input" value={templateEditorState.domainProfileKey} onChange={(event) => updateTemplateField('domainProfileKey', event.target.value)} maxLength={100} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label} htmlFor="template-consultation-type">{t('consultationType', { defaultValue: 'Consultation type' })}</label>
                <select id="template-consultation-type" className="text-input" value={templateEditorState.appointmentTypeId} onChange={(event) => updateTemplateField('appointmentTypeId', event.target.value)}>
                  <option value="">{t('allConsultationTypes', { defaultValue: 'All consultation types' })}</option>
                  {appointmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('subjectType', { defaultValue: 'Subject type' })}</label>
                <input className="text-input" value={templateEditorState.subjectType} onChange={(event) => updateTemplateField('subjectType', event.target.value)} maxLength={50} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('description', { defaultValue: 'Description' })}</label>
                <textarea className="text-input" rows={3} value={templateEditorState.description} onChange={(event) => updateTemplateField('description', event.target.value)} maxLength={1000} />
              </div>

              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={templateEditorState.isActive} onChange={(event) => updateTemplateField('isActive', event.target.checked)} />
                <span>{t('templateActive', { defaultValue: 'Template is active and available during consultation creation' })}</span>
              </label>
            </div>

            <div className={styles.footer}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleApproveTemplate}
                disabled={!selectedTemplate || !selectedTemplate.createdByAi || selectedTemplate.aiStatus !== 'pendingApproval' || savingTemplate || savingAssignment}
              >
                {t('approve', { defaultValue: 'Approve' })}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleRejectTemplate}
                disabled={!selectedTemplate || !selectedTemplate.createdByAi || selectedTemplate.aiStatus !== 'pendingApproval' || savingTemplate || savingAssignment}
              >
                {t('reject', { defaultValue: 'Reject' })}
              </button>
              <button className="btn btn-secondary" type="button" onClick={handleCreateNewTemplate} disabled={savingTemplate || savingAssignment}>
                {t('resetForm', { defaultValue: 'Reset form' })}
              </button>
              <button
                className="btn btn-danger"
                type="button"
                onClick={handleDeleteTemplate}
                disabled={!selectedTemplate || savingTemplate || savingAssignment}
              >
                {t('deleteTemplate', { defaultValue: 'Delete template' })}
              </button>
              <button className="btn btn-primary" type="button" onClick={handleSaveTemplate} disabled={savingTemplate || savingAssignment}>
                {savingTemplate
                  ? t('saving', { ns: 'common', defaultValue: 'Saving…' })
                  : selectedTemplateId
                    ? t('saveChanges', { defaultValue: 'Save changes' })
                    : t('createTemplate', { defaultValue: 'Create template' })}
              </button>
            </div>

            <div className={styles.assignmentSection}>
              <div className={styles.panelHeader}>
                <h2>{t('templateFieldAssignments', { defaultValue: 'Template field assignments' })}</h2>
                <span className={styles.count}>{selectedTemplate?.fields?.length ?? 0}</span>
              </div>

              {!selectedTemplateId ? (
                <div className={styles.empty}>{t('saveTemplateBeforeAssignments', { defaultValue: 'Save the template first, then assign field definitions to it.' })}</div>
              ) : (
                <>
                  <div className={styles.assignmentComposer}>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('fieldDefinition', { defaultValue: 'Field definition' })}</label>
                      <select className="text-input" value={assignmentEditorState.fieldDefinitionId} onChange={(event) => updateAssignmentField('fieldDefinitionId', event.target.value)}>
                        <option value="">{t('selectFieldDefinition', { defaultValue: 'Select field definition' })}</option>
                        {availableFieldDefinitions.map((definition) => (
                          <option key={definition.id} value={definition.id}>{definition.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className={styles.assignmentGrid}>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>{t('sectionKey', { defaultValue: 'Section key' })}</label>
                        <input className="text-input" value={assignmentEditorState.sectionKey} onChange={(event) => updateAssignmentField('sectionKey', event.target.value)} maxLength={100} />
                      </div>

                      <div className={styles.fieldRow}>
                        <label className={styles.label}>{t('sortOrder', { defaultValue: 'Sort order' })}</label>
                        <input className="text-input" type="number" value={assignmentEditorState.sortOrder} onChange={(event) => updateAssignmentField('sortOrder', event.target.value)} />
                      </div>

                      <label className={styles.checkboxRow}>
                        <input type="checkbox" checked={assignmentEditorState.isRequired} onChange={(event) => updateAssignmentField('isRequired', event.target.checked)} />
                        <span>{t('requiredInTemplate', { defaultValue: 'Required in this template' })}</span>
                      </label>
                    </div>

                    <div className={styles.footerInline}>
                      <button className="btn btn-primary" type="button" onClick={handleAddAssignment} disabled={savingAssignment || availableFieldDefinitions.length === 0}>
                        {savingAssignment ? t('saving', { ns: 'common', defaultValue: 'Saving…' }) : t('assignFieldDefinition', { defaultValue: 'Assign field definition' })}
                      </button>
                    </div>
                  </div>

                  {selectedTemplate?.fields && selectedTemplate.fields.length > 0 ? (
                    <div className={styles.assignmentList}>
                      {selectedTemplate.fields
                        .slice()
                        .sort((left, right) => left.sortOrder - right.sortOrder)
                        .map((templateField) => {
                          const definition = getFieldDefinition(templateField.fieldDefinitionId);
                          return (
                            <TemplateFieldAssignmentCard
                              key={templateField.id}
                              templateField={templateField}
                              definition={definition}
                              onUpdate={handleUpdateAssignment}
                              onRemove={handleRemoveAssignment}
                              saving={savingAssignment}
                            />
                          );
                        })}
                    </div>
                  ) : (
                    <div className={styles.empty}>{t('templateNoAssignedFields', { defaultValue: 'No field definitions are assigned to this template yet.' })}</div>
                  )}
                </>
              )}
            </div>
          </section>
        </section>
      )}
    </div>
  );
};

type AssignmentCardProps = {
  templateField: ConsultationTemplateField;
  definition: ConsultationFieldDefinition | null;
  onUpdate: (templateField: ConsultationTemplateField, input: UpdateConsultationTemplateFieldInput) => Promise<void>;
  onRemove: (templateField: ConsultationTemplateField) => Promise<void>;
  saving: boolean;
};

const TemplateFieldAssignmentCard: React.FC<AssignmentCardProps> = ({ templateField, definition, onUpdate, onRemove, saving }) => {
  const { t } = useTranslation(['consultations', 'calendar', 'common']);
  const [sectionKey, setSectionKey] = React.useState(templateField.sectionKey ?? '');
  const [sortOrder, setSortOrder] = React.useState(String(templateField.sortOrder));
  const [isRequired, setIsRequired] = React.useState(templateField.isRequired);

  React.useEffect(() => {
    setSectionKey(templateField.sectionKey ?? '');
    setSortOrder(String(templateField.sortOrder));
    setIsRequired(templateField.isRequired);
  }, [templateField]);

  const handleSave = React.useCallback(async () => {
    const parsedSortOrder = Number(sortOrder);
    await onUpdate(templateField, {
      sectionKey: normalizeOptionalText(sectionKey),
      sortOrder: Number.isFinite(parsedSortOrder) ? parsedSortOrder : templateField.sortOrder,
      isRequired,
    });
  }, [isRequired, onUpdate, sectionKey, sortOrder, templateField]);

  return (
    <div className={styles.assignmentCard}>
      <div className={styles.assignmentHeader}>
        <div>
          <strong>{definition?.name || templateField.fieldDefinitionId}</strong>
          <p className={styles.assignmentHint}>{definition?.description || t('noDescription', { defaultValue: 'No description' })}</p>
        </div>
        <span className={styles.badgeMuted}>{definition?.fieldType || t('fieldDefinition', { defaultValue: 'Field definition' })}</span>
      </div>

      <div className={styles.assignmentGridCompact}>
        <div className={styles.fieldRow}>
          <label className={styles.label}>{t('sectionKey', { defaultValue: 'Section key' })}</label>
          <input className="text-input" value={sectionKey} onChange={(event) => setSectionKey(event.target.value)} maxLength={100} />
        </div>

        <div className={styles.fieldRow}>
          <label className={styles.label}>{t('sortOrder', { defaultValue: 'Sort order' })}</label>
          <input className="text-input" type="number" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} />
        </div>

        <label className={styles.checkboxRow}>
          <input type="checkbox" checked={isRequired} onChange={(event) => setIsRequired(event.target.checked)} />
          <span>{t('requiredInTemplate', { defaultValue: 'Required in this template' })}</span>
        </label>
      </div>

      <div className={styles.footerInline}>
        <button className="btn btn-danger" type="button" onClick={() => void onRemove(templateField)} disabled={saving}>
          {t('remove', { ns: 'common', defaultValue: 'Remove' })}
        </button>
        <button className="btn btn-primary" type="button" onClick={() => void handleSave()} disabled={saving}>
          {t('saveChanges', { defaultValue: 'Save changes' })}
        </button>
      </div>
    </div>
  );
};

export default ConsultationTemplatesPage;